"use client";

import { useEffect, useMemo, useState } from "react";
import { LinkPreviewCard } from "./LinkPreviewCard";
import { extractFirstOutboundUrl } from "@/lib/post-link-extract";

/**
 * EPIC-022 STORY-622 — render a server-sanitized post body.
 *
 * The HTML coming back from `/v1/posts/:id` is generated by the
 * server-side `renderMarkdownToSafeHtml` (escape-then-render pattern,
 * known-safe tag set). Trusted-by-construction, rendered with
 * `dangerouslySetInnerHTML`. The component name is the only place we
 * acknowledge that "yes, we're injecting HTML, and yes, that's
 * deliberate" — the `<PostBody>` boundary is the audit point.
 *
 * EPIC-046 part 2 phase F — when the body contains an outbound
 * http(s) link, render an OG preview card below the body for the
 * FIRST eligible URL. Caller can opt out via `previewOutboundLinks={false}`
 * (e.g. unauth blur view, embeds, edit-preview).
 */
export function PostBody({
  html,
  className = "",
  previewOutboundLinks = true,
}: {
  html: string;
  className?: string;
  previewOutboundLinks?: boolean;
}) {
  // 2026-05-04 — hydration mismatch fix. Reading window.location.origin
  // at render time gives empty on SSR but a real origin on client first
  // paint, which mismatches at the LinkPreviewCard's href and trips
  // React #418. Defer to useEffect so SSR + first client render match.
  const [ownOrigin, setOwnOrigin] = useState("");
  useEffect(() => {
    setOwnOrigin(window.location.origin);
  }, []);
  const previewUrl = useMemo(() => {
    if (!previewOutboundLinks) return null;
    return extractFirstOutboundUrl(html, ownOrigin ? [ownOrigin] : []);
  }, [html, previewOutboundLinks, ownOrigin]);

  return (
    <div className={className}>
      <div
        className="post-body text-[14px] leading-relaxed"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {previewUrl && <LinkPreviewCard url={previewUrl} />}
    </div>
  );
}
